import { create } from 'zustand'

import { determineSystemSelection } from '@/lib/auth/system-selection'
import { isTokenExpiringSoon } from '@/lib/auth/token'
import { type User } from '@/models/domain/user'
import { authService } from '@/services/auth-service'

interface AuthState {
  user: User | null
  selectedSystem: string | null
  isAuthenticated: boolean
  needsSystemSelection: boolean
  isLoading: boolean
  error: string | null
  showExpiryWarning: boolean
  sessionExpired: boolean
}

interface AuthActions {
  setUser: (user: User) => void
  logout: () => void
  loadUser: () => Promise<void>
  selectSystem: (system: string) => void
  clearError: () => void
  checkTokenExpiry: () => void
  setShowExpiryWarning: (show: boolean) => void
  setSessionExpired: () => void
}

type AuthStore = AuthState & AuthActions

function resolveSystemForUser(
  user: User,
  currentSelectedSystem: string | null
): { finalSelectedSystem: string | null; needsSystemSelection: boolean } {
  const { selectedSystem, needsSelection } = determineSystemSelection(user)
  const finalSelectedSystem = currentSelectedSystem || selectedSystem
  return {
    finalSelectedSystem,
    needsSystemSelection: needsSelection && !finalSelectedSystem,
  }
}

function buildAuthenticatedState(
  user: User,
  currentSelectedSystem: string | null
): Partial<AuthState> {
  const { finalSelectedSystem, needsSystemSelection } = resolveSystemForUser(
    user,
    currentSelectedSystem
  )
  return {
    user,
    selectedSystem: finalSelectedSystem,
    isAuthenticated: true,
    needsSystemSelection,
    isLoading: false,
  }
}

async function fetchAndSetUser(
  set: (state: Partial<AuthStore>) => void,
  get: () => AuthStore
): Promise<void> {
  const user = await authService.getCurrentUser()

  if (get().user) {
    set({ isLoading: false })
    return
  }

  if (!user) {
    set({ user: null, isAuthenticated: false, isLoading: false })
    return
  }

  set(buildAuthenticatedState(user, get().selectedSystem))
}

const initialState: AuthState = {
  user: null,
  selectedSystem: localStorage.getItem('selected_system') || null,
  isAuthenticated: false,
  needsSystemSelection: false,
  isLoading: false,
  error: null,
  showExpiryWarning: false,
  sessionExpired: false,
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  setUser: (user: User) => {
    const { finalSelectedSystem, needsSystemSelection } = resolveSystemForUser(
      user,
      get().selectedSystem
    )
    set({
      user,
      selectedSystem: finalSelectedSystem,
      isAuthenticated: true,
      needsSystemSelection,
      isLoading: false,
      error: null,
      showExpiryWarning: false,
      sessionExpired: false,
    })
  },

  selectSystem: (system: string) => {
    const user = get().user
    if (user) {
      const userPreferenceKey = `system_preference_${user.email}`
      localStorage.setItem(userPreferenceKey, system)
    }
    localStorage.setItem('selected_system', system)
    set({
      selectedSystem: system,
      isAuthenticated: true,
      needsSystemSelection: false,
    })
  },

  logout: () => {
    const selectedSystem = get().selectedSystem
    authService.logout()
    set({
      user: null,
      selectedSystem,
      isAuthenticated: false,
      needsSystemSelection: false,
      error: null,
      showExpiryWarning: false,
    })
  },

  loadUser: async () => {
    if (get().isLoading) return
    if (get().user) return

    if (!authService.validateToken()) {
      set({ user: null, isAuthenticated: false, isLoading: false })
      return
    }

    set({ isLoading: true })

    try {
      await fetchAndSetUser(set, get)
    } catch {
      if (get().user) {
        set({ isLoading: false })
        return
      }
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },

  clearError: () => {
    set({ error: null })
  },
  checkTokenExpiry: () => {
    set({ showExpiryWarning: isTokenExpiringSoon() })
  },
  setShowExpiryWarning: (show: boolean) => {
    set({ showExpiryWarning: show })
  },
  setSessionExpired: () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_token_expiry')
    set({ sessionExpired: true, showExpiryWarning: false })
  },
}))
